//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import cloneDeep from 'lodash/fp/cloneDeep';

import { zip } from '@utils/arrayUtils';
import { isDefined } from '@utils/filterPredicates';
import { getAllUrlsFromString } from '@utils/linkify';
import { commaSeparatedNumber, readablePrice } from '@utils/readableNumber';
import { standardSort } from '@utils/sortBy';

export const shopifyShopUrlRegex =
  /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+)\.myshopify\.com$/;

export const hashtagRegex =
  /(?:#)((?:\w|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|(?:[\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u2388\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u2618\u261D\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638-\u263A\u2648-\u2653\u2660\u2663\u2665\u2666\u2668\u267B\u267F\u2692-\u2694\u2696\u2697\u2699\u269B\u269C\u26A0\u26A1\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4\u26C5\u26C8\u26CE\u26CF\u26D1\u26D3\u26D4\u26E9\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|\uD83C[\uDC04\uDCCF\uDD70\uDD71\uDD7E\uDD7F\uDD8E\uDD91-\uDD9A\uDE01\uDE02\uDE1A\uDE2F\uDE32-\uDE3A\uDE50\uDE51\uDF00-\uDF21\uDF24-\uDF93\uDF96\uDF97\uDF99-\uDF9B\uDF9E-\uDFF0\uDFF3-\uDFF5\uDFF7-\uDFFF]|\uD83D[\uDC00-\uDCFD\uDCFF-\uDD3D\uDD49-\uDD4E\uDD50-\uDD67\uDD6F\uDD70\uDD73-\uDD79\uDD87\uDD8A-\uDD8D\uDD90\uDD95\uDD96\uDDA5\uDDA8\uDDB1\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDEF\uDDF3\uDDFA-\uDE4F\uDE80-\uDEC5\uDECB-\uDED0\uDEE0-\uDEE5\uDEE9\uDEEB\uDEEC\uDEF0\uDEF3]|\uD83E[\uDD10-\uDD18\uDD80-\uDD84\uDDC0]|(?:0\u20E3|1\u20E3|2\u20E3|3\u20E3|4\u20E3|5\u20E3|6\u20E3|7\u20E3|8\u20E3|9\u20E3|#\u20E3|\\*\u20E3|\uD83C(?:\uDDE6\uD83C(?:\uDDEB|\uDDFD|\uDDF1|\uDDF8|\uDDE9|\uDDF4|\uDDEE|\uDDF6|\uDDEC|\uDDF7|\uDDF2|\uDDFC|\uDDE8|\uDDFA|\uDDF9|\uDDFF|\uDDEA)|\uDDE7\uD83C(?:\uDDF8|\uDDED|\uDDE9|\uDDE7|\uDDFE|\uDDEA|\uDDFF|\uDDEF|\uDDF2|\uDDF9|\uDDF4|\uDDE6|\uDDFC|\uDDFB|\uDDF7|\uDDF3|\uDDEC|\uDDEB|\uDDEE|\uDDF6|\uDDF1)|\uDDE8\uD83C(?:\uDDF2|\uDDE6|\uDDFB|\uDDEB|\uDDF1|\uDDF3|\uDDFD|\uDDF5|\uDDE8|\uDDF4|\uDDEC|\uDDE9|\uDDF0|\uDDF7|\uDDEE|\uDDFA|\uDDFC|\uDDFE|\uDDFF|\uDDED)|\uDDE9\uD83C(?:\uDDFF|\uDDF0|\uDDEC|\uDDEF|\uDDF2|\uDDF4|\uDDEA)|\uDDEA\uD83C(?:\uDDE6|\uDDE8|\uDDEC|\uDDF7|\uDDEA|\uDDF9|\uDDFA|\uDDF8|\uDDED)|\uDDEB\uD83C(?:\uDDF0|\uDDF4|\uDDEF|\uDDEE|\uDDF7|\uDDF2)|\uDDEC\uD83C(?:\uDDF6|\uDDEB|\uDDE6|\uDDF2|\uDDEA|\uDDED|\uDDEE|\uDDF7|\uDDF1|\uDDE9|\uDDF5|\uDDFA|\uDDF9|\uDDEC|\uDDF3|\uDDFC|\uDDFE|\uDDF8|\uDDE7)|\uDDED\uD83C(?:\uDDF7|\uDDF9|\uDDF2|\uDDF3|\uDDF0|\uDDFA)|\uDDEE\uD83C(?:\uDDF4|\uDDE8|\uDDF8|\uDDF3|\uDDE9|\uDDF7|\uDDF6|\uDDEA|\uDDF2|\uDDF1|\uDDF9)|\uDDEF\uD83C(?:\uDDF2|\uDDF5|\uDDEA|\uDDF4)|\uDDF0\uD83C(?:\uDDED|\uDDFE|\uDDF2|\uDDFF|\uDDEA|\uDDEE|\uDDFC|\uDDEC|\uDDF5|\uDDF7|\uDDF3)|\uDDF1\uD83C(?:\uDDE6|\uDDFB|\uDDE7|\uDDF8|\uDDF7|\uDDFE|\uDDEE|\uDDF9|\uDDFA|\uDDF0|\uDDE8)|\uDDF2\uD83C(?:\uDDF4|\uDDF0|\uDDEC|\uDDFC|\uDDFE|\uDDFB|\uDDF1|\uDDF9|\uDDED|\uDDF6|\uDDF7|\uDDFA|\uDDFD|\uDDE9|\uDDE8|\uDDF3|\uDDEA|\uDDF8|\uDDE6|\uDDFF|\uDDF2|\uDDF5|\uDDEB)|\uDDF3\uD83C(?:\uDDE6|\uDDF7|\uDDF5|\uDDF1|\uDDE8|\uDDFF|\uDDEE|\uDDEA|\uDDEC|\uDDFA|\uDDEB|\uDDF4)|\uDDF4\uD83C\uDDF2|\uDDF5\uD83C(?:\uDDEB|\uDDF0|\uDDFC|\uDDF8|\uDDE6|\uDDEC|\uDDFE|\uDDEA|\uDDED|\uDDF3|\uDDF1|\uDDF9|\uDDF7|\uDDF2)|\uDDF6\uD83C\uDDE6|\uDDF7\uD83C(?:\uDDEA|\uDDF4|\uDDFA|\uDDFC|\uDDF8)|\uDDF8\uD83C(?:\uDDFB|\uDDF2|\uDDF9|\uDDE6|\uDDF3|\uDDE8|\uDDF1|\uDDEC|\uDDFD|\uDDF0|\uDDEE|\uDDE7|\uDDF4|\uDDF8|\uDDED|\uDDE9|\uDDF7|\uDDEF|\uDDFF|\uDDEA|\uDDFE)|\uDDF9\uD83C(?:\uDDE9|\uDDEB|\uDDFC|\uDDEF|\uDDFF|\uDDED|\uDDF1|\uDDEC|\uDDF0|\uDDF4|\uDDF9|\uDDE6|\uDDF3|\uDDF7|\uDDF2|\uDDE8|\uDDFB)|\uDDFA\uD83C(?:\uDDEC|\uDDE6|\uDDF8|\uDDFE|\uDDF2|\uDDFF)|\uDDFB\uD83C(?:\uDDEC|\uDDE8|\uDDEE|\uDDFA|\uDDE6|\uDDEA|\uDDF3)|\uDDFC\uD83C(?:\uDDF8|\uDDEB)|\uDDFD\uD83C\uDDF0|\uDDFE\uD83C(?:\uDDF9|\uDDEA)|\uDDFF\uD83C(?:\uDDE6|\uDDF2|\uDDFC))))[\ufe00-\ufe0f\u200d]?)+)/g;

/**
 * Email pattern to match a valid email address.
 * It includes characters, digits, dots, underscores, and hyphens in both the local and domain parts.
 *
 * @type {string}
 */
const emailPattern = '[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\\.[a-zA-Z0-9._-]+';

/**
 * Regex to validate a single email address.
 * Ensures that the entire string matches a valid email format.
 *
 * @example
 * const valid = emailRegex.test("john_doe@example.com");
 * console.log(valid); // true
 *
 * @type {RegExp}
 */
export const emailRegex = new RegExp(`^${emailPattern}$`, 'i');

// Mega regex that matches either an Instagram mention or hashtag. It was adapted
// from `hashtagRegex` and `igMentionRegex` above, except it also captures the '#'
// character preceding a hashtag so we can check the first character of the captured
// value to easily determine whether it's a mention or hashtag.
const igMentionOrHashtagRegex =
  /(?:^|\s)(@[A-Za-z0-9_\-.]+[A-Za-z0-9_])|(#(?:\w|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|(?:[\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u2388\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u2618\u261D\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638-\u263A\u2648-\u2653\u2660\u2663\u2665\u2666\u2668\u267B\u267F\u2692-\u2694\u2696\u2697\u2699\u269B\u269C\u26A0\u26A1\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4\u26C5\u26C8\u26CE\u26CF\u26D1\u26D3\u26D4\u26E9\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|\uD83C[\uDC04\uDCCF\uDD70\uDD71\uDD7E\uDD7F\uDD8E\uDD91-\uDD9A\uDE01\uDE02\uDE1A\uDE2F\uDE32-\uDE3A\uDE50\uDE51\uDF00-\uDF21\uDF24-\uDF93\uDF96\uDF97\uDF99-\uDF9B\uDF9E-\uDFF0\uDFF3-\uDFF5\uDFF7-\uDFFF]|\uD83D[\uDC00-\uDCFD\uDCFF-\uDD3D\uDD49-\uDD4E\uDD50-\uDD67\uDD6F\uDD70\uDD73-\uDD79\uDD87\uDD8A-\uDD8D\uDD90\uDD95\uDD96\uDDA5\uDDA8\uDDB1\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDEF\uDDF3\uDDFA-\uDE4F\uDE80-\uDEC5\uDECB-\uDED0\uDEE0-\uDEE5\uDEE9\uDEEB\uDEEC\uDEF0\uDEF3]|\uD83E[\uDD10-\uDD18\uDD80-\uDD84\uDDC0]|(?:0\u20E3|1\u20E3|2\u20E3|3\u20E3|4\u20E3|5\u20E3|6\u20E3|7\u20E3|8\u20E3|9\u20E3|#\u20E3|\\*\u20E3|\uD83C(?:\uDDE6\uD83C(?:\uDDEB|\uDDFD|\uDDF1|\uDDF8|\uDDE9|\uDDF4|\uDDEE|\uDDF6|\uDDEC|\uDDF7|\uDDF2|\uDDFC|\uDDE8|\uDDFA|\uDDF9|\uDDFF|\uDDEA)|\uDDE7\uD83C(?:\uDDF8|\uDDED|\uDDE9|\uDDE7|\uDDFE|\uDDEA|\uDDFF|\uDDEF|\uDDF2|\uDDF9|\uDDF4|\uDDE6|\uDDFC|\uDDFB|\uDDF7|\uDDF3|\uDDEC|\uDDEB|\uDDEE|\uDDF6|\uDDF1)|\uDDE8\uD83C(?:\uDDF2|\uDDE6|\uDDFB|\uDDEB|\uDDF1|\uDDF3|\uDDFD|\uDDF5|\uDDE8|\uDDF4|\uDDEC|\uDDE9|\uDDF0|\uDDF7|\uDDEE|\uDDFA|\uDDFC|\uDDFE|\uDDFF|\uDDED)|\uDDE9\uD83C(?:\uDDFF|\uDDF0|\uDDEC|\uDDEF|\uDDF2|\uDDF4|\uDDEA)|\uDDEA\uD83C(?:\uDDE6|\uDDE8|\uDDEC|\uDDF7|\uDDEA|\uDDF9|\uDDFA|\uDDF8|\uDDED)|\uDDEB\uD83C(?:\uDDF0|\uDDF4|\uDDEF|\uDDEE|\uDDF7|\uDDF2)|\uDDEC\uD83C(?:\uDDF6|\uDDEB|\uDDE6|\uDDF2|\uDDEA|\uDDED|\uDDEE|\uDDF7|\uDDF1|\uDDE9|\uDDF5|\uDDFA|\uDDF9|\uDDEC|\uDDF3|\uDDFC|\uDDFE|\uDDF8|\uDDE7)|\uDDED\uD83C(?:\uDDF7|\uDDF9|\uDDF2|\uDDF3|\uDDF0|\uDDFA)|\uDDEE\uD83C(?:\uDDF4|\uDDE8|\uDDF8|\uDDF3|\uDDE9|\uDDF7|\uDDF6|\uDDEA|\uDDF2|\uDDF1|\uDDF9)|\uDDEF\uD83C(?:\uDDF2|\uDDF5|\uDDEA|\uDDF4)|\uDDF0\uD83C(?:\uDDED|\uDDFE|\uDDF2|\uDDFF|\uDDEA|\uDDEE|\uDDFC|\uDDEC|\uDDF5|\uDDF7|\uDDF3)|\uDDF1\uD83C(?:\uDDE6|\uDDFB|\uDDE7|\uDDF8|\uDDF7|\uDDFE|\uDDEE|\uDDF9|\uDDFA|\uDDF0|\uDDE8)|\uDDF2\uD83C(?:\uDDF4|\uDDF0|\uDDEC|\uDDFC|\uDDFE|\uDDFB|\uDDF1|\uDDF9|\uDDED|\uDDF6|\uDDF7|\uDDFA|\uDDFD|\uDDE9|\uDDE8|\uDDF3|\uDDEA|\uDDF8|\uDDE6|\uDDFF|\uDDF2|\uDDF5|\uDDEB)|\uDDF3\uD83C(?:\uDDE6|\uDDF7|\uDDF5|\uDDF1|\uDDE8|\uDDFF|\uDDEE|\uDDEA|\uDDEC|\uDDFA|\uDDEB|\uDDF4)|\uDDF4\uD83C\uDDF2|\uDDF5\uD83C(?:\uDDEB|\uDDF0|\uDDFC|\uDDF8|\uDDE6|\uDDEC|\uDDFE|\uDDEA|\uDDED|\uDDF3|\uDDF1|\uDDF9|\uDDF7|\uDDF2)|\uDDF6\uD83C\uDDE6|\uDDF7\uD83C(?:\uDDEA|\uDDF4|\uDDFA|\uDDFC|\uDDF8)|\uDDF8\uD83C(?:\uDDFB|\uDDF2|\uDDF9|\uDDE6|\uDDF3|\uDDE8|\uDDF1|\uDDEC|\uDDFD|\uDDF0|\uDDEE|\uDDE7|\uDDF4|\uDDF8|\uDDED|\uDDE9|\uDDF7|\uDDEF|\uDDFF|\uDDEA|\uDDFE)|\uDDF9\uD83C(?:\uDDE9|\uDDEB|\uDDFC|\uDDEF|\uDDFF|\uDDED|\uDDF1|\uDDEC|\uDDF0|\uDDF4|\uDDF9|\uDDE6|\uDDF3|\uDDF7|\uDDF2|\uDDE8|\uDDFB)|\uDDFA\uD83C(?:\uDDEC|\uDDE6|\uDDF8|\uDDFE|\uDDF2|\uDDFF)|\uDDFB\uD83C(?:\uDDEC|\uDDE8|\uDDEE|\uDDFA|\uDDE6|\uDDEA|\uDDF3)|\uDDFC\uD83C(?:\uDDF8|\uDDEB)|\uDDFD\uD83C\uDDF0|\uDDFE\uD83C(?:\uDDF9|\uDDEA)|\uDDFF\uD83C(?:\uDDE6|\uDDF2|\uDDFC))))[\ufe00-\ufe0f\u200d]?)+)/g;

/**
 * Replaces all hashtag occurrences in a string with a given callback function.
 * @param   str         The string to replace hashtags from.
 * @param   cb          {function(string)} A callback that provides the replacement for a match.
 * @returns {string}    The specified string with all hashtags replaced by the callback function.
 */
export function replaceHashtags(
  str: string,
  cb: (s: string) => string,
): string {
  return str.replace(hashtagRegex, cb);
}

/**
 * Returns a list of the hashtags in a given string.
 *
 * @param str The string to replace hashtags from
 * @returns The list of hashtags parsed from the given string
 */
export function parseHashtags(str: string): string[] {
  return str.match(hashtagRegex) ?? [];
}

export function isNullOrEmpty(str: string) {
  return !str || str.trim().length === 0;
}

/**
 * Low-effort way to pluralize a string. Will use a naive approach of
 * appending an 's' if the count provided is greater than 1, but can
 * have custom plural forms provided if need be.
 * @example pluralize(1, 'apple') = '1 apple'
 * @example pluralize(3, 'apple') = '3 apples'
 * @example pluralize(2, 'person', 'people') = '2 people'
 */
export function pluralize(
  count: number,
  singularForm: string,
  pluralForm?: string,
  shouldIncludeCount: boolean = true,
): string {
  if (count === 1) {
    return `${shouldIncludeCount ? `${count} ` : ''}${singularForm}`;
  } else {
    return `${shouldIncludeCount ? `${commaSeparatedNumber(count)} ` : ''}${
      pluralForm || singularForm + 's'
    }`;
  }
}

/**
 * Pluralize a range expression.
 * e.g. "Items 1-10 [of 34]" vs "Item 21 [of 21]".
 */
export function pluralizeRangeExpression(
  start: number,
  end: number,
  singularForm: string,
  pluralForm?: string,
): string {
  const count = end - start + 1;

  if (count === 1) {
    return `${singularForm} ${start}`;
  } else {
    return `${pluralForm || singularForm + 's'} ${start}-${end}`;
  }
}

export function capitalize(str: string, allWords?: boolean): string {
  if (allWords) {
    return str
      .split(' ')
      .map((word) => capitalize(word, false))
      .join(' ');
  } else {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

/**
 * Shortens the provided string and appends an ellipsis
 *
 * @param str The string to shorten
 * @param maxLength The maximum length of the shortened string
 * @param breakAtBlankSpace If true, cutoff the string at the index of the last ' ' character
 * that occurs before the character at index `maxLength`
 *
 * @returns {string} The original string, shortened with an `...` appended
 */
export function ellipsize(
  str: string,
  options: {
    maxLength: number;
    breakAtBlankSpace?: boolean;
    noSpaceBeforeEllipsis?: boolean;
  },
) {
  const { breakAtBlankSpace, maxLength, noSpaceBeforeEllipsis } = options;
  if (str.length > maxLength) {
    const strCutAtMaxLength = str.slice(0, maxLength);
    const ellipsis = noSpaceBeforeEllipsis ? '...' : ' ...';
    if (breakAtBlankSpace) {
      const strCutAtBlankSpace = strCutAtMaxLength.slice(
        0,
        strCutAtMaxLength.lastIndexOf(' '),
      );
      return `${strCutAtBlankSpace}${ellipsis}`;
    } else {
      return `${strCutAtMaxLength}${ellipsis}`;
    }
  } else {
    return str;
  }
}

/**
 * Formats a list of strings as a grammatically correct string
 *
 * @param list The list of strings to format
 * @returns {string} One string with every element of the list separated by commas
 */
export function toGrammaticallyCorrectList(words: string[]): string {
  switch (words.length) {
    case 0:
      return '';
    case 1:
      return words[0] ?? '';
    case 2:
      return `${words[0]} and ${words[1]}`;
    default:
      return `${words.slice(0, -1).join(', ')}, and ${words[words.length - 1]}`;
  }
}

/**
 * Returns correct 'and' or ',' or '' to insert BEFORE a word in a list of words
 * Example use case is mapping over an array where each word is wrapped in a Button.
 *
 */
export function getGrammaticallyCorrectConnector(
  index: number,
  numWords: number,
): string {
  if (index === 0) {
    return '';
  }

  if (numWords === 2 && index === 1) {
    return ' and ';
  }

  if (index === numWords - 1) {
    return ', and ';
  }

  return ', ';
}

type IParsedSocialTextType = 'text' | 'mention' | 'hashtag' | 'link';

export interface IParsedSocialTextValue {
  type: IParsedSocialTextType;
  value: string;
}

/**
 * Takes text as input and parses it to return a representation as an array of typed
 * objects. For example, the input 'Hello @curalate! #shippy https://link.com' would result in output:
 * [
 *   { type: 'text', value: 'Hello '},
 *   { type: 'mention', value: '@curalate'},
 *   { type: 'text', value: '! '},
 *   { type: 'hashtag', value: '#shippy'}
 *   { type: 'link', value: 'https://link.com'}
 * ]
 *
 * Note: This approach was adapted from the parseSocialText util in pinsights-js-shared
 *
 * @param text The string to parse.
 *
 * @returns IParsedSocialTextValue[] The parsed array of objects with properties
 * { type, value }, where type is one of ('hashtag', 'link', 'mention',' text').
 */
export const parseSocialText: (text: string) => IParsedSocialTextValue[] = (
  text,
) => {
  const allUrls = getAllUrlsFromString(text) ?? [];

  const generateSegmentObj: (
    type: IParsedSocialTextType,
    value: string,
  ) => IParsedSocialTextValue = (type, value) => {
    return {
      type,
      value,
    };
  };

  const nextMatch = (
    remainingText: string,
  ): {
    index: number;
    type: IParsedSocialTextType;
    value: string;
  } | null => {
    const mentionOrHashtagMatchIndex = remainingText.search(
      igMentionOrHashtagRegex,
    );

    const urlMatchIndex = allUrls[0]
      ? allUrls[0].index - (text.length - remainingText.length)
      : -1;

    if (
      mentionOrHashtagMatchIndex > -1 &&
      (urlMatchIndex === -1 || mentionOrHashtagMatchIndex < urlMatchIndex)
    ) {
      // Grab either a mention or a hashtag capture
      const mentionOrHashtagMatch =
        remainingText.match(igMentionOrHashtagRegex)?.[0] ?? '';

      const valueCapture = mentionOrHashtagMatch.trim();
      const matchOffset = mentionOrHashtagMatch.length - valueCapture.length;

      return {
        index: mentionOrHashtagMatchIndex + matchOffset,
        type: valueCapture.startsWith('#') ? 'hashtag' : 'mention',
        value: valueCapture,
      };
    }

    if (urlMatchIndex > -1) {
      // Grab a link capture
      const urlMatch = allUrls.shift();
      return {
        index: urlMatchIndex,
        type: 'link',
        value: urlMatch?.url ?? '',
      };
    }

    return null;
  };

  let remainingText = text;
  const result: IParsedSocialTextValue[] = [];

  while (remainingText.length > 0) {
    const match = nextMatch(remainingText);

    if (!match) {
      // No match, so extract the rest of the string as text.
      const remainingTextSegmentObj = generateSegmentObj('text', remainingText);
      result.push(remainingTextSegmentObj);
      remainingText = remainingText.slice(remainingText.length);

      break;
    }

    if (match.index > 0) {
      // Text precedes our match, so extract it.
      const precedingText = remainingText.slice(0, match.index);
      const precedingTextSegmentObj = generateSegmentObj('text', precedingText);
      result.push(precedingTextSegmentObj);
    }

    const socialTextSegmentObj = generateSegmentObj(match.type, match.value);
    result.push(socialTextSegmentObj);

    remainingText = remainingText.slice(match.index + match.value.length);
  }

  return result;
};

/**
 * Mimics the behavior of the optionalNumber angular filter
 *
 * Adheres to UTF8 guidelines for Data Point Not Applicable
 *
 * toFixed is not used here it pads numbers with 0s below the given fractionSize
 *
 * @param val The number to parse
 * @param fractionSize Optionally, the number of decimal places to limit the resulting string to
 *  Defaults to 3 to maintain angular filter behavior
 *  https://docs.angularjs.org/api/ng/filter/number
 *
 * @returns string Either the expected symbol for an invalid number, or the number to the decimal place specified
 */
export const getOptionalNumberStr = (
  val: number | undefined,
  fractionSize = 3,
): string => {
  if (val === undefined || getOptionalNumberIsInvalid(val)) {
    return '--';
  }

  const decimals = val - Math.floor(val); // Will take the form of 0.XXX
  if (decimals.toString().length - 2 > fractionSize) {
    // Eval the length without 0. in 0.XXX
    return val.toFixed(fractionSize);
  } else {
    return val.toString();
  }
};

/**
 * Mimics angular's "optionalNumber" evaluation of a potentially invalid number
 *
 * @param val The number to evaluate
 *
 * @returns boolean Whether the number has been determined to be invalid
 */
export const getOptionalNumberIsInvalid = (
  val: number | undefined,
  isNegativeOneValid: boolean = false,
): boolean => {
  return (
    typeof val !== 'number' || isNaN(val) || (!isNegativeOneValid && val === -1)
  );
};

/**
 * Mimics the behavior of the optionalNumber angular filter by returning a data placeholder when data is unavailable
 *
 * Should data be available as a number, this will return the result of readablePrice which formats the number as currency.
 */
export const getOptionalPriceStr = (
  val: number | undefined,
  hideDecimal?: boolean,
  currencyFormat?: string,
): string => {
  if (val === undefined || getOptionalNumberIsInvalid(val)) {
    return '--';
  } else {
    return readablePrice(val, hideDecimal, currencyFormat);
  }
};

export const convertAllFieldsToString = (o: any) => {
  if (typeof o === 'object' && o) {
    const clonedO = cloneDeep(o);
    Object.keys(clonedO).forEach((k) => {
      if (clonedO[k] !== null && typeof clonedO[k] === 'object') {
        return convertAllFieldsToString(clonedO[k]);
      }
      clonedO[k] = `${clonedO[k]}`;
    });
    return clonedO;
  }
  return o;
};

/**
 * Low-effort way to add tense to a string. Will use a naive approach of
 * appending an 'ed' if the inPast is true, but can have custom forms
 * provided if need be.
 * @example addTense(false, 'connect') = 'connect'
 * @example addTense(true, 'connect') = 'connected'
 * @example addTense(true, 'swim', 'swam') = 'swam'
 */
export function addTense(
  pastTense: boolean,
  presentTenseForm: string,
  pastTenseForm?: string,
): string {
  if (!pastTense) {
    return presentTenseForm;
  } else {
    return `${pastTenseForm || presentTenseForm + 'ed'}`;
  }
}

// Regex to match spaces and special characters not allowed in instagram hashtags.
// Excludes "_" since this is an allowed character.
//
// This was implemented so that we can allow foreign characters hashtags in the stream drawer,
// while still disallowing the most used special characters.
//
// We display an error if a hashtag input matches this regex:
// if (regex.test) { displayError() }
//
// However, a more robust regex would be the inverse of this, and include only allowed characters.
// If anyone wants to create a regex to match all english letters, numbers, foreign characters, emoji,
// and the "_" character, this could potentially be replaced and a negation could be added:
// if (!regex.test) { displayError() }
export const instagramHashtagSpecialCharacterRegex =
  // eslint-disable-next-line no-useless-escape
  /[`~!@#$%^&*\(\)\-\+={\[\}\]|\:;"'<,>\.?\/\s]/;

/**
 * Regex to match the general pattern of a deets item id
 * An id consists of 5 blocks of lowercase letters or numbers separated by a '-' character
 *
 * @example baf0c213-4a8f-449d-a80d-4f830182c690
 */
export const deetsIdRegex = /[a-f0-9]+-[a-f0-9]+-[a-f0-9]+-[a-f0-9]+-[a-f0-9]+/;

/**
 * Given an array of indices, returns an array of substrings of the given string split
 * at the given boundaries. This helper ignores negative boundaries.
 *
 * Ex: splitStringByBoundaries([1,5,7,8], 'abcdefghijk') --> ['a', 'bcde', 'fg', 'h', 'ijk']
 */
export const splitStringByBoundaries = (boundaries: number[], str: string) => {
  // Sort the given boundaries so we accurately pull out substrings, and filter out undefined vals.
  const sortedBoundaries = boundaries.sort(standardSort).filter((b) => b > 0);
  // Create pairs representing the start/end indices for all of our desired substrings.
  // ex: [1,5,7] --> [0,1], [1,5], [5,7], [7, undefined]
  const pairs = zip([0, ...sortedBoundaries], sortedBoundaries);
  // Map over the pairs to get all desired substrings, and filter out empty strings in case
  // the given boundary set included either the start or end index
  return pairs.map(([start, end]) => str.slice(start, end)).filter((s) => s);
};

/**
 * Given an array of boundary pair tuples representing the desired start/end of subsections of
 * a string, returns an array of substrings of the given string split by the given boundaries.
 *
 * Ex: splitStringByBoundaryPairs([[9,18], [22, 29]], 'a string ISOLATEME to isolate') --> ['a string ', 'ISOLATEME', ' to ', 'isolate']
 */
export const splitStringByBoundaryPairs = (
  boundaryPairs: [number, number][],
  str: string,
) => {
  return splitStringByBoundaries(boundaryPairs.flat(), str);
};

function hasDefinedMatchIndex(
  match: RegExpMatchArray,
): match is RegExpMatchArray & { index: number } {
  return isDefined(match.index);
}

/**
 * Given a string and a regular expression, returns an array of tuples representing the start/end
 * indices of regex matches within the string. Easily coupled with the splitStringByBoundaryPairs
 * util to separate out regex matches as independent substrings within a string.
 *
 * Note: the end boundary is not inclusive. This allows it to be used directly to slice a substring.
 *
 * Ex: findBoundariesWithRegex('Wow #cosmo is a good boy', hashtagRegex) ==> [[4, 10]]
 */
export const findBoundariesWithRegex = (
  str: string,
  regex: RegExp,
): [number, number][] => {
  const matchArray = Array.from(str.matchAll(regex)).filter(
    hasDefinedMatchIndex,
  );
  return matchArray.map(
    (m) => [m.index, m.index + m[0].length] as [number, number],
  );
};

/**
 * Escapes special characters for use in RexEx
 *
 * source MDN: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
 */
function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Given a substring (a string to search for within a whole string) and a whole string, returns
 * an array of strings split into chunks by the substring.
 *
 * It supports case-sensitive and case-insensitive searches. Defaults to case-insensitive.
 *
 * Ex: case-insensitive - splitStringBySubstring('test', 'oneTeststrinGtest') ==> ['one', 'Test', 'strinG', 'test']
 * Ex: case-sensitive - splitStringBySubstring('test', 'oneTeststrinG') ==> ['oneTeststrinG', 'test']
 *
 */
export const chunkStringBySubstring = (
  substring: string,
  wholeString: string,
  options?: {
    caseSensitive?: boolean;
  },
): string[] => {
  if (substring === '') {
    return [wholeString];
  }

  const escapedSubstring = escapeRegExp(substring);
  const chunkedString = wholeString.split(
    new RegExp(`(${escapedSubstring})`, options?.caseSensitive ? 'g' : 'gi'),
  );

  return chunkedString.filter((chunk) => chunk !== '');
};

/**
 * Converts a given size in bytes to a readable string in MB, KB, or B.
 *
 * The function determines whether the size should be represented in megabytes (MB), kilobytes (KB), or bytes (B)
 * based on its value, and then returns the size as the floor number value with the appropriate unit as a string.
 *
 * @param {number} sizeInBytes - The size in bytes to be converted.
 * @returns {string} A string representing the size in either MB or KB.
 *
 * @example
 * convertBytesToReadableSize(1048576); // "1MB"
 * convertBytesToReadableSize(2048);    // "2KB"
 * convertBytesToReadableSize(3500);    // "3KB" (correct value = 3.41, but fn. retunrs floor value)
 * convertBytesToReadableSize(789);    // "789B"
 */
export const convertBytesToReadableSize = (sizeInBytes: number): string => {
  const oneKBUnit = 1024;
  const oneMBUnit = oneKBUnit * oneKBUnit;

  return sizeInBytes >= oneMBUnit
    ? `${Math.floor(sizeInBytes / oneMBUnit)}MB`
    : sizeInBytes >= oneKBUnit
      ? `${Math.floor(sizeInBytes / oneKBUnit)}KB`
      : `${sizeInBytes}B`;
};

/**
 * Given a list of label strings, returns the first 25 labels that contain with the searchString
 * or the whole list of labels if the query string is empty
 *
 * @param labels A list of labels to search
 * @param searchString A substring we should search for in each label
 */
export const containsStringSearch = (
  labels: string[],
  searchString: string,
): string[] => {
  return searchString.length > 0
    ? labels
        .filter((label) =>
          label.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()),
        )
        .slice(0, 25)
    : labels;
};
